import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PurchaseOrderDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/PurchesOrders/purchase-order-dto';
import { PurchaseOrderLineItemDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/PurchaseOrderLineItems/purchase-order-line-item-dto';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { TaxRateV2Dto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/TaxRatesV2/tax-rate-v-2-dto';
import { CurrentVenueHelperService } from 'src/app/share-modul/services/current-venue-helper/current-venue.helper.service';
import { Subscription, range } from 'rxjs';
import { GetPurchaseOrder$Params } from 'src/api/generated/fn/inventory/get-purchase-order';
import { InventoryService, TaxRateV2Service } from 'src/api/generated/services';
import { AuthenticationService } from 'src/app/share-modul/services/authentication/authentication.service';
import { GetTaxRates$Params } from 'src/api/generated/fn/tax-rate-v-2/get-tax-rates';
import { ReceivePurchaseOrder$Params } from 'src/api/generated/fn/inventory/receive-purchase-order';
import { ReceivePurchaseOrderDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/PurchesOrders/receive-purchase-order-dto';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { MenuCurrencyDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Menu/menu-currency-dto';
import { CurrencyService } from 'src/app/pos-panel/services/currency-helper/currency.service';

export interface PurchaseOrderLineItemDtoCalculations extends PurchaseOrderLineItemDto{
   taxAmount: number;
   grossAmount: number;
}

@Component({
  selector: 'app-purchase-order-receipt',
  templateUrl: './purchase-order-receipt.component.html',
  styleUrls: ['./purchase-order-receipt.component.scss']
})
export class PurchaseOrderReceiptComponent implements OnInit, OnDestroy {

  venueId: string;
  inventoryId: string;
  purchaseOrderId: string;
  purchaseOrderDto: ReceivePurchaseOrderDto = {} as ReceivePurchaseOrderDto;
  
  purchaseOrder: PurchaseOrderDto = {} as PurchaseOrderDto;
  selectedItems: Array<PurchaseOrderLineItemDtoCalculations> = new Array<PurchaseOrderLineItemDtoCalculations>(); 
  taxRates: Array<TaxRateV2Dto>;
  venueIdUpdateSubscription: Subscription;
  form: FormGroup;
  currency: MenuCurrencyDto;
  timezoneOffset: any;

  constructor(public dialogService: DialogService, 
              private formBuilder: FormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private confirmationService: ConfirmationService,
              public translate: TranslateService,
              private currentVenueHelper: CurrentVenueHelperService,
              public currencyService: CurrencyService,
              private inventoryService: InventoryService,
              private authenticationService: AuthenticationService,
              private taxRateService: TaxRateV2Service,
              private messageService: MessageService) {

    this.form = new FormGroup({
        invoiceNumber: new FormControl('', [Validators.required]),
        items: new FormArray([])
    });    
    this.currencyService.currencyObservable().subscribe(currency => this.currency = currency);
    const companyId = this.authenticationService.getCompanyId();
    const taxRatesParams: GetTaxRates$Params = { companyId: companyId, isRemoved: false };
    this.taxRateService.getTaxRates(taxRatesParams).subscribe( response => this.taxRates = response.taxRates ? response.taxRates : [] );
    
   }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['venue-id'] != null) {
        this.venueId = params['venue-id'];
        this.currentVenueHelper.updateCurrentVenueIdDropdown(params['venue-id']);
        this.currentVenueHelper.getTimezone().subscribe(tzOffset => this.timezoneOffset = tzOffset)
      }
      if (params['inventory-id'] != null && params['purchase-order-id'] != null) {
        this.inventoryId = params['inventory-id'];
        this.purchaseOrderId = params['purchase-order-id'];
        
        const getParams: GetPurchaseOrder$Params = { inventoryId: this.inventoryId, purchaseOrderId: this.purchaseOrderId };
        this.inventoryService.getPurchaseOrder(getParams).subscribe( response => {
          this.purchaseOrder = response;
           this.createForm();  
          });
      }
    });
  
  }

  private createForm(): void {
      this.form = new FormGroup({
        invoiceNumber: new FormControl('', [Validators.required]),
        items: this.formBuilder.array([])
      });   
      this.purchaseOrder.lineItems?.forEach(item => this.items.push(this.createTableRow(item)))
    }

    private createTableRow(item: PurchaseOrderLineItemDto): FormGroup {
      return this.formBuilder.group({
        stockItem: new FormControl(item.stockItem, {
          validators: []
        }),
       quantityReceived: new FormControl('', {
          validators: []
        }),
        netPrice: new FormControl('', {
          validators: []
        }),
        taxRate: new FormControl('', {
          validators: []
        }),
        taxAmount: new FormControl('', {
          validators: []
        }), 
        grossAmount: new FormControl('', {
          validators: []
        })
      }, [this.priceValidator]);
    }

  selectRow(event: any){

    let index = event.index;
    (this.items.controls[index] as FormGroup).setValidators(this.priceValidator as ValidatorFn);

    (this.items.controls[index] as FormGroup).controls['quantityReceived'].setValidators(Validators.required)
    this.getItemControls(index).controls['quantityReceived'].updateValueAndValidity();

    (this.items.controls[index] as FormGroup).controls['netPrice'].setValidators(Validators.required)
    this.getItemControls(index).controls['netPrice'].updateValueAndValidity();
    
    (this.items.controls[index] as FormGroup).controls['taxRate'].setValidators(Validators.required)
    this.getItemControls(index).controls['taxRate'].updateValueAndValidity();

    (this.items.controls[index] as FormGroup).controls['taxAmount'].setValidators(Validators.required)
    this.getItemControls(index).controls['taxAmount'].updateValueAndValidity();

    (this.items.controls[index] as FormGroup).controls['grossAmount'].setValidators(Validators.required)
    this.getItemControls(index).controls['grossAmount'].updateValueAndValidity();
  }

  unselectRow(event: any){

    let index = event.index;
    (this.items.controls[index] as FormGroup).removeValidators(this.priceValidator as ValidatorFn);
    (this.items.controls[index] as FormGroup).updateValueAndValidity();
    
    this.getItemControls(index).controls['quantityReceived'].removeValidators(Validators.required);
    this.getItemControls(index).controls['quantityReceived'].updateValueAndValidity();

    this.getItemControls(index).controls['netPrice'].removeValidators(Validators.required);
    this.getItemControls(index).controls['netPrice'].updateValueAndValidity();
    
    this.getItemControls(index).controls['taxRate'].removeValidators(Validators.required);
    this.getItemControls(index).controls['taxRate'].updateValueAndValidity();

    this.getItemControls(index).controls['taxAmount'].removeValidators(Validators.required);
    this.getItemControls(index).controls['taxAmount'].updateValueAndValidity();

    this.getItemControls(index).controls['grossAmount'].removeValidators(Validators.required);
    this.getItemControls(index).controls['grossAmount'].updateValueAndValidity();
  }

  onSelectChange(event: any){
    if(event.checked){
      range(0, this.items.controls.length).forEach(index => this.selectRow({ index: index }))
    }else{
      range(0, this.items.controls.length).forEach(index => this.unselectRow({ index: index }))
    }
  }
  
  get items(): FormArray {
     return this.form.get('items') as FormArray;
  }
  getItemControls(index: number){
    return (this.items.controls[index] as FormGroup)
  }

  receiveOrder(){
    if(this.form.valid){
      this.confirmationService.confirm({
        header: this.translate.instant('PURCHASE_ORDER.CONFIRMATION_DIALOG_RECIEVE_ORDER_TITLE'),
        message: this.translate.instant('PURCHASE_ORDER.CONFIRMATION_DIALOG_RECIEVE_ORDER_QUESTION'),
        acceptLabel: this.translate.instant('YES'),
        rejectLabel: this.translate.instant('NO'),
        defaultFocus: "reject",
        acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn constructive-btn',
        rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
        accept: () => {
        const selectedItems = this.selectedItems.map(item => item.stockItem?.id);
        var formItems = this.form.getRawValue().items.filter((item: any) => selectedItems.indexOf(item.stockItem.id) != -1);
      
        const orderParam: ReceivePurchaseOrderDto = { invoiceNumber: this.form.getRawValue().invoiceNumber, lineItems: formItems ? formItems.map((element: any) => element as PurchaseOrderLineItemDto) : [] };
          const params: ReceivePurchaseOrder$Params = { inventoryId: this.inventoryId, purchaseOrderId: this.purchaseOrderId, body: orderParam }
          this.inventoryService.receivePurchaseOrder(params).subscribe({
            next: response => {
              this.messageService.add( { severity: 'success', summary: 'Purchase order was successfully recieved!', life: 3000 } );
              this.navigateToMain();
            },
              error: errorResponse => this.messageService.add( { severity: 'error', summary: 'Error occurred!', life: 3000 } )
          })
        }
      });
    }
  }

  public priceValidator(group: FormGroup): ValidationErrors | null {
    const netPrice = parseFloat(group.get('netPrice')?.value);
    const taxRate = group.get('taxRate')?.value;
    const taxAmount = parseFloat(group.get('taxAmount')?.value);
    const grossAmount = parseFloat(group.get('grossAmount')?.value);


    if( taxRate != undefined && taxRate.rate != undefined && (Math.abs(grossAmount - netPrice - netPrice * taxRate.rate/100) > 1e-6 || Math.abs(taxAmount - netPrice * taxRate.rate / 100 ) > 1e-6)){
        return { wrongCalculations: true };
    }
    return null;
  }

  navigateToMain() {
    this.router.navigate([this.venueId, "inventory", this.inventoryId], { queryParams: { activeIndex: 1 }});
  }

  public ngOnDestroy(): void {
    if (this.venueIdUpdateSubscription) {
      this.venueIdUpdateSubscription.unsubscribe();
    }
  }
}